/* eslint-env browser */
import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import AddisDateTimeField from './AddisDateTimeField';
import { FieldLabel } from './FormKit';
import { tokens as c } from '../../theme';

/**
 * A date filter of a list or report page: its label above an Addis date box
 * (the day only), and an X beside the box once a date is set that clears it
 * in one click (call-centre feedback, 29 Oct 2026: every search and filter
 * box clears with one X). The date box itself never empties (it ignores a
 * deleted date), so without the X a date filter could only be undone from
 * the active-filter chips. The X keeps its place while there is nothing to
 * clear, so the boxes beside it never move when a date is picked; after a
 * clear the focus goes back to the date box.
 *
 *   <DateFilterField id="tx-from" label={t('start_date')} value={fromMs}
 *     onChange={setFromMs} onClear={() => setFromMs(null)} />
 *
 * value: epoch ms (or empty); onChange gets the start of the picked Addis day.
 */
export default function DateFilterField({ id, label, value, onChange, onClear, sx }) {
  const { t } = useTranslation();
  const has = value !== null && value !== undefined && value !== '';
  const clearLabel = t('clear_value', { what: label, defaultValue: 'Clear {{what}}' });
  const clear = () => {
    if (onClear) onClear();
    const box = document.getElementById(id);
    if (box && typeof box.focus === 'function') box.focus();
  };
  return (
    <Box sx={sx}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <AddisDateTimeField id={id} dateOnly value={has ? value : null} onChange={onChange} />
        <Tooltip title={has ? clearLabel : ''}>
          <IconButton
            onClick={clear}
            aria-label={clearLabel}
            aria-hidden={has ? undefined : 'true'}
            tabIndex={has ? 0 : -1}
            sx={{ width: 44, height: 44, color: c.INK_2, visibility: has ? 'visible' : 'hidden' }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
